/**
 * @file ClientSettingImports.js
 * @description This file contains the ClientSettingImports component, which is a reusable form for displaying and editing an client's import information.
 */

import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
} from '../../../services/clientService';
import Toast from '../../ui/toasts/Toast';
import {
    SecurityAttributes,
    ClientOptionEnum,
} from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/Contexts';
import ClientOptionButtons from './ClientOptionButtons';
import { getText } from '../../../i18n';

const ClientSettingImports = () => {
    const [
        initialAutoOrgLevelCreationSetting,
        setInitialAutoOrgLevelCreationSetting,
    ] = useState({});
    const [selectedAutoOrgLevelCreation, setSelectedAutoOrgLevelCreation] =
        useState();
    const [
        initialReviewDemographicImports,
        setInitialReviewDemographicImports,
    ] = useState();
    const [reviewDemographicImports, setReviewDemographicImports] = useState();
    const [initialReviewEnrollmentImports, setInitialReviewEnrollmentImports] =
        useState();
    const [reviewEnrollmentImports, setReviewEnrollmentImports] = useState();
    const [
        initialEnableEmailAndPhoneValidation,
        setInitialEnableEmailAndPhoneValidation,
    ] = useState();
    const [enableEmailAndPhoneValidation, setEnableEmailAndPhoneValidation] =
        useState();

    const { hasAttribute } = useAuthContext();

    const getOptions = async () => {
        try {
            const data = await getClientOptions(0, [
                ClientOptionEnum.CreateOrgLevelsOnImport,
                ClientOptionEnum.ReviewDemographicImports,
                ClientOptionEnum.ReviewEnrollmentImports,
                ClientOptionEnum.EnableEmailAndPhoneValidation,
            ]);

            const autoOrgLevelCreationObject = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.CreateOrgLevelsOnImport,
            );

            const demographicImport = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.ReviewDemographicImports,
            );

            const enrollmentImport = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.ReviewEnrollmentImports,
            );

            const enableEmailAndPhoneValidationOption = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.EnableEmailAndPhoneValidation,
            );

            if (
                autoOrgLevelCreationObject &&
                demographicImport &&
                enrollmentImport &&
                enableEmailAndPhoneValidationOption
            ) {
                setInitialAutoOrgLevelCreationSetting(
                    autoOrgLevelCreationObject,
                );
                setSelectedAutoOrgLevelCreation(
                    autoOrgLevelCreationObject.value,
                );

                setInitialReviewDemographicImports(demographicImport);
                setReviewDemographicImports(demographicImport.value);

                setInitialReviewEnrollmentImports(enrollmentImport);
                setReviewEnrollmentImports(enrollmentImport.value);

                setInitialEnableEmailAndPhoneValidation(
                    enableEmailAndPhoneValidationOption,
                );
                setEnableEmailAndPhoneValidation(
                    enableEmailAndPhoneValidationOption.value,
                );
            } else
                Toast.error(
                    getText('msg_9831b17d23cd'),
                );
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        getOptions();
    }, []);

    const handleSave = async () => {
        try {
            var autoOrgLevelCreationOption = await handleSaveOption(
                initialAutoOrgLevelCreationSetting,
                selectedAutoOrgLevelCreation,
                setInitialAutoOrgLevelCreationSetting,
            );

            var demoImportReview = await handleSaveOption(
                initialReviewDemographicImports,
                reviewDemographicImports,
                setInitialReviewDemographicImports,
            );

            var enrollmentImportReview = await handleSaveOption(
                initialReviewEnrollmentImports,
                reviewEnrollmentImports,
                setInitialReviewEnrollmentImports,
            );

            var enableEmailAndPhoneValidationOption = await handleSaveOption(
                initialEnableEmailAndPhoneValidation,
                enableEmailAndPhoneValidation,
                setInitialEnableEmailAndPhoneValidation,
            );

            await Promise.all([
                autoOrgLevelCreationOption,
                demoImportReview,
                enrollmentImportReview,
            ]);
            Toast.success(getText('msg_3fd8174532ee'));
        } catch (error) {
            console.error('Error saving import client settings:', error);
        }
    };

    const handleSaveOption = async (
        initialOption,
        newValue,
        setInitialOptionFunction,
    ) => {
        const option = {
            clientOptionId: initialOption.clientOptionId,
            value: `${newValue}`,
        };

        if (initialOption.usingDefaultValue) {
            const response = await createClientOption(option, 0);
            if (response.error)
                return Toast.error(
                    getText('msg_46c4bd3f2c45'),
                );
            setInitialOptionFunction(response.data);
        } else if (initialOption.value != `${newValue}`) {
            const response = await updateClientOption(option, 0);
            if (response.error)
                return Toast.error(
                    getText('msg_f861dcfa033d'),
                );
            setInitialOptionFunction(response.data);
        }
    };

    const handleCancel = () => {
        setSelectedAutoOrgLevelCreation(
            initialAutoOrgLevelCreationSetting.value,
        );
        setReviewDemographicImports(initialReviewDemographicImports.value);
        setReviewEnrollmentImports(initialReviewEnrollmentImports.value);
        setEnableEmailAndPhoneValidation(
            initialEnableEmailAndPhoneValidation.value,
        );
    };

    return (
        <>
            <div
                style={{
                    marginLeft: '48px',
                    marginRight: '48px',
                    marginTop: '12px',
                }}
            >
                <div style={{ marginTop: '12px' }}>{getText('msg_747eb3bc1ad5')}</div>

                <div style={{ marginLeft: '24px' }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className={`form-switch`}>
                            <input
                                onClick={() =>
                                    setSelectedAutoOrgLevelCreation(
                                        selectedAutoOrgLevelCreation === 'true'
                                            ? 'false'
                                            : 'true',
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={
                                    selectedAutoOrgLevelCreation === 'true'
                                }
                            />
                        </div>
                        <div>
                            {getText('msg_783de51fa8e8')}
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '24px' }}>{getText('msg_55d6ef06ea35')}</div>
                <div
                    style={{
                        color: 'var(--iNGAGED-Neutral-Color2)',
                        marginBottom: '12px',
                    }}
                >
                    {getText('msg_61493300df31')}
                </div>

                <div style={{ marginLeft: '24px' }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className={`form-switch`}>
                            <input
                                onClick={() =>
                                    setReviewDemographicImports(
                                        reviewDemographicImports === 'true'
                                            ? 'false'
                                            : 'true',
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={reviewDemographicImports === 'true'}
                            />
                        </div>
                        <div>{getText('msg_a4cd85b9a010')}</div>
                    </div>
                </div>

                <div style={{ marginLeft: '24px' }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className={`form-switch`}>
                            <input
                                onClick={() =>
                                    setReviewEnrollmentImports(
                                        reviewEnrollmentImports === 'true'
                                            ? 'false'
                                            : 'true',
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={reviewEnrollmentImports === 'true'}
                            />
                        </div>
                        <div>{getText('msg_38a3114e0346')}</div>
                    </div>
                </div>

                <div style={{ marginTop: '12px' }}>{getText('msg_dd74d182c641')}</div>

                <div style={{ marginLeft: '24px' }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className={`form-switch`}>
                            <input
                                onClick={() =>
                                    setEnableEmailAndPhoneValidation(
                                        enableEmailAndPhoneValidation === 'true'
                                            ? 'false'
                                            : 'true',
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={
                                    enableEmailAndPhoneValidation === 'true'
                                }
                            />
                        </div>
                        <div>{getText('msg_e04550e1d1f1')}</div>
                    </div>
                </div>
            </div>
            <div>
                {hasAttribute(SecurityAttributes.ClientSettingsFullAccess) && (
                    <ClientOptionButtons
                        handleSave={handleSave}
                        handleCancel={handleCancel}
                    />
                )}
            </div>
        </>
    );
};

export default ClientSettingImports;
